/**
 * g1xl-v2#1053 Part 3 — the dashboard's feed declaration, line-scoped.
 *
 * The board draws, per game, a moneyline pair and (Spread/Total reading)
 * the three lines around the Kalshi strike nearest even money. Declaring
 * `sports:[CFB]` asked the owning edge for the whole alternate ladder
 * (~15,000 rows on he2 since Poly's 2026-10-01 listing change). This hook
 * declares what the board draws, by the first of these that applies:
 *
 *   1. the owner advertised the proposed line-scope axis
 *      (`activeSports.filterAxes` ∋ `'lineScope'`) → `{sports, lineScope:
 *      'main'}` and the engine picks the lines (no engine does yet);
 *   2. the owner is an edge → a light REST index of the slate
 *      (`lib/feedFilter/boardIndex`), the pick (`boardLines`), and the rows
 *      on the `tickers` axis beside the sport fold — re-picked every
 *      `BOARD_INDEX_REFRESH_MS` so a main line that moved a strike is
 *      followed;
 *   3. otherwise, only for a core- or firehose-served sport (or the
 *      feature off) → the sport-wide declaration the board made before.
 *
 * An EDGE-served sport is never declared whole (the 2026-10-02 wedge, again
 * on gbuff 2026-10-13: a slow index, an index error or an owner flap fell
 * back to `sports:[CFB]` — he2's whole ~14,000-row slate — at the foreground
 * cadence). Instead:
 *   - owner unknown, index slow, index error, no REST origin → the board
 *     stays MUTED (an empty allow-list: the edge connection is narrow and
 *     quiet; the core floor carries the sport's rows, so the board is not
 *     dark) and retries with backoff;
 *   - a selection that already has a pick KEEPS it through owner flaps (an
 *     edge restart drops its announce), refresh errors and slow refreshes —
 *     a re-pick replaces a pick only with a newer pick. The US owner's names
 *     too.
 * Cold start: while the first answer is on the way the muted step is
 * `provisional` (the transports keep the previous scope on the wire), and
 * "Loading" ends after `BOARD_INDEX_WAIT_MS` — the board stays muted and the
 * fetch continues.
 *
 * Hidden tab: the scope never changes while the document is hidden. A
 * refresh or owner change that comes due while hidden is deferred to the
 * return; only the FIRST pick of a selection proceeds (a board opened in a
 * background tab must still declare something narrow).
 *
 * The cycle itself (owner resolve → index → pick → hold / backoff /
 * hidden-defer) is `edgeIndexCycle`, shared with a `/game/*` page naming its
 * game's rows (`useRouteSportFeedNeeds`); this hook says what the board
 * fetches and declares.
 *
 * Narrowing a re-pick rides the aggregator's flicker hold and the worker's
 * one-replace-per-second limiter (Part 2b); widening is immediate.
 */
import { useEffect, useRef } from 'react';
import { declareFeedNeeds } from '@/stores/feedNeeds';
import { feedFilterBoardEnabled } from '@/clients/feedFilterConfig';
import { fetchMarketsIndex } from '@/lib/feedFilter/boardIndex';
import { boardGameKeys, pickBoardTickers, pickUsLineTickers, type BoardReading } from '@/lib/feedFilter/boardLines';
import { useMarketStore } from '@/stores/marketStore';
import {
  INDEX_REFRESH_MS, INDEX_RETRY_MS, INDEX_WAIT_MS, resolveIndexOwner, startIndexCycle,
  type IndexCycleMode, type IndexOwner,
} from './edgeIndexCycle';
import { feedNeedsKey } from './useFeedNeeds';
import type { FeedNeeds } from '@/workers/feedFilter';
import type { FeedVenue } from '@/stores/feedPairOwnership';
import { normalizeUsGameKey, usGameSportForKey, usPeriodForMarketType } from '@/lib/polymarketUs/sports';
import type { HydraMarket } from '@/clients/HydraClient';
import type { Sport } from '@/views/ValueDashboard/types';

export const BOARD_INDEX_REFRESH_MS = INDEX_REFRESH_MS;
/** The Value Dashboard's feed-need source id (the board and its Spread/Total
 *  view both address it). */
export const VALUE_DASHBOARD_FEED_SOURCE = 'value-dashboard';

// ── Full ladder for the games a trader works (Dan, 2026-10-03) ──────────
// The pick streams three lines per game from the edge; the rest of a game's
// ladder rides the floor (hc1) at its standby cadence. A trader working a
// game — stepping its line selector, expanding it — needs every strike of
// THAT game live from the edge (gbuff he2: alternate CFB strikes went stale
// or missing while being traded). The Spread/Total view publishes those
// games' tickers here; the board declares them beside its pick. Per game,
// never the slate — the slate's full ladder is what the pick exists to avoid.
const _fullLadder = new Map<string, readonly string[]>();
const _fullLadderListeners = new Map<string, Set<() => void>>();
export function setBoardFullLadderTickers(sourceId: string, tickers: readonly string[]): void {
  const next = [...new Set(tickers)].sort();
  const prev = _fullLadder.get(sourceId) ?? [];
  if (prev.length === next.length && prev.every((t, i) => t === next[i])) return;
  if (next.length === 0) _fullLadder.delete(sourceId);
  else _fullLadder.set(sourceId, next);
  for (const fn of [...(_fullLadderListeners.get(sourceId) ?? [])]) fn();
}
export function getBoardFullLadderTickers(sourceId: string): readonly string[] {
  return _fullLadder.get(sourceId) ?? [];
}
function onFullLadderChange(sourceId: string, fn: () => void): () => void {
  let set = _fullLadderListeners.get(sourceId);
  if (!set) { set = new Set(); _fullLadderListeners.set(sourceId, set); }
  set.add(fn);
  return () => { set!.delete(fn); };
}
/** The pick plus the worked games' ladders, sorted and deduped. */
function withFullLadders(sourceId: string, pick: readonly string[]): string[] {
  const extra = _fullLadder.get(sourceId);
  return extra && extra.length > 0 ? [...new Set([...pick, ...extra])].sort() : [...pick];
}
/** How long the first index of a selection may take before the board stops
 *  saying "Loading" (it stays muted — never broad — and keeps waiting). */
export const BOARD_INDEX_WAIT_MS = INDEX_WAIT_MS;
/** Retry delays after an index failure (or no REST origin); the last one
 *  repeats. A held pick stays declared meanwhile. */
export const BOARD_INDEX_RETRY_MS = INDEX_RETRY_MS;

export interface BoardFeedNeedsParams {
  /** Scope the board: one binary-game sport on a game reading, not an
   *  all-sports or discovery board. False declares `broad` verbatim. */
  scoped: boolean;
  /** The board's underlying sport (its main-line feed key). */
  sport: Sport | null;
  reading: BoardReading;
  /** The board's inclusive slate window; null = every game of the sport. */
  dayWindow: { startIso: string; endIso: string } | null;
  /** The declaration the board makes when it is not line-scoped — also the
   *  FOLD beside the allow-list (what carries the rows next to a
   *  ticker-broad sibling in another tab). */
  broad: FeedNeeds;
}

export type BoardFeedMode = IndexCycleMode;

export interface BoardFeedStatus {
  mode: BoardFeedMode;
  owner: string | null;
  tickers: number;
  games: number;
  maxRowsPerGame: number;
  error: string | null;
  indexedAtMs: number | null;
  /** True from a selection change until the board knows what it declares
   *  for it (the index landed, or it fell back): the "Loading" signal the
   *  empty states read (`lib/feedFilter/feedSettling`). */
  pending: boolean;
  /** #1231 — the US line add-on on the Spread/Total reading: names declared,
   *  and the US owner's last spread/total index read (rows, monotonic ms).
   *  The PM measurement reads these. */
  usLines?: { names: number; indexRows: number | null; indexMs: number | null; error: string | null };
}

const _status = new Map<string, BoardFeedStatus>();
const initialStatus = (): BoardFeedStatus => ({ mode: 'off', owner: null, tickers: 0, games: 0, maxRowsPerGame: 0, error: null, indexedAtMs: null, pending: false });

/** Diagnostics for the health pane / tests — what the board declared last. */
export function getBoardFeedNeedsStatus(sourceId: string): BoardFeedStatus {
  return _status.get(sourceId) ?? initialStatus();
}

/** #1071 — the role of the feed that owns `sport` on `venue` right now, or
 *  null when the store names no owner. A core- or firehose-owned sport's
 *  Kalshi books carry no traded volume, which is why the initial-line count
 *  splits its timeouts by this. */
export function boardFeedOwnerRole(sport: Sport, venue?: FeedVenue): IndexOwner['role'] | null {
  return resolveIndexOwner(sport, venue)?.role ?? null;
}

/** Catalogue identity only: no REST price becomes quote/execution evidence.
 * The K/Intl index names the displayed slate; US adds just its full-game
 * moneyline tickers for those same canonical games, including the CFB alias. */
function usBoardTickers(rows: readonly HydraMarket[], gameKeys: readonly string[]): string[] {
  const games = new Set(gameKeys.map(key => {
    const sport = usGameSportForKey(key);
    return sport ? normalizeUsGameKey(key, sport) : null;
  }).filter((key): key is string => key !== null));
  return rows.filter(row => {
    const r = row as unknown as Record<string, unknown>;
    const sport = typeof r.canonicalGameKeyV2 === 'string' ? usGameSportForKey(r.canonicalGameKeyV2) : null;
    const key = sport ? normalizeUsGameKey(r.canonicalGameKeyV2, sport) : null;
    return r.source === 'polymarket_us' && r.venue === 'polymarket_us'
      && r.marketType === 'game' && r.pmusMarketType === 'moneyline'
      && typeof r.sportsMarketType === 'string' && usPeriodForMarketType(r.sportsMarketType) === 'full'
      && (r.period === undefined || r.period === 'full') && r.status === 'open'
      && typeof r.pmusMarketSlug === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(r.pmusMarketSlug)
      && r.ticker === `PMUS-${r.pmusMarketSlug}` && key !== null && games.has(key)
      && typeof r.canonicalYesTeam === 'string' && key.split('|').slice(3).includes(r.canonicalYesTeam);
  }).map(row => row.ticker);
}

/** What one board index answers: the pick, and the moneyline rows it came
 *  from (they name the slate's games for the US owner's add-on). */
interface BoardIndexPick { pick: ReturnType<typeof pickBoardTickers>; moneyline: HydraMarket[]; rows: HydraMarket[] }

const isUsRow = (m: HydraMarket): boolean => (m as unknown as { source?: unknown }).source === 'polymarket_us';
/** The game keys (V2) of the index rows a worked game's tickers belong to. */
function workedGameKeys(rows: readonly HydraMarket[], workedTickers: readonly string[]): Set<string> {
  if (workedTickers.length === 0) return new Set();
  const worked = new Set(workedTickers), games = new Set<string>();
  for (const m of rows) if (worked.has(m.ticker) && m.canonicalGameKeyV2) games.add(m.canonicalGameKeyV2);
  return games;
}

export function useBoardFeedNeeds(sourceId: string, params: BoardFeedNeedsParams): void {
  const { scoped, sport, reading, dayWindow, broad } = params;
  // The cadence is NOT part of what the index is fetched for: a focused game
  // (Part 2d, 1000 → 250) re-declares the last needs with the new cadence
  // instead of re-running the effect, which used to re-download the whole
  // index (12 MB on he2's CFB Spread/Total) on every focus click.
  const cadence = broad.cadenceMs;
  const broadKey = feedNeedsKey({ ...broad, cadenceMs: undefined });
  const windowKey = dayWindow ? `${dayWindow.startIso}..${dayWindow.endIso}` : '';
  const cadenceRef = useRef(cadence);
  const lastRef = useRef<{ needs: FeedNeeds; key: string } | null>(null);
  // Declared BEFORE the main effect so a combined change sees the new cadence there.
  useEffect(() => {
    cadenceRef.current = cadence;
    const last = lastRef.current;
    if (last == null) return;
    const full: FeedNeeds = { ...last.needs, cadenceMs: cadence };
    const key = feedNeedsKey(full);
    if (key === last.key) return;
    lastRef.current = { needs: last.needs, key };
    declareFeedNeeds(sourceId, full);
  }, [sourceId, cadence]);
  useEffect(() => {
    const status = initialStatus();
    const setStatus = (patch: Partial<BoardFeedStatus>): void => { Object.assign(status, patch); _status.set(sourceId, { ...status }); };
    const declareNeeds = (needs: FeedNeeds): void => {
      const full: FeedNeeds = { ...needs, cadenceMs: cadenceRef.current };
      const key = feedNeedsKey(full);
      const changed = lastRef.current?.key !== key;
      lastRef.current = { needs, key };
      if (changed) declareFeedNeeds(sourceId, full);
    };
    if (!scoped || !sport || !feedFilterBoardEnabled()) {
      declareNeeds(broad);
      setStatus({ mode: 'off' });
      return;
    }
    const wire = sport.toUpperCase();
    // #1231 — US line rows held for local re-picks (a worked game joins with no
    // fetch), and the K/Intl rows that map a worked ticker to its game.
    let heldRows: HydraMarket[] = [];
    let heldUsLines: HydraMarket[] = [];
    const lines = reading === 'spread-total';
    const identityPick = `${sourceId}:pick`, identityUs = `${sourceId}:us`;
    // Set by the cleanup FIRST: a read in flight when the board unmounts or
    // switches selection (its abort lands in a catch, or it settles late) must
    // not republish identity or status that the cleanup just cleared.
    let stopped = false;
    const store = () => useMarketStore.getState();
    const usWorkedNames = (): string[] => {
      if (!lines) return [];
      const games = workedGameKeys(heldRows, getBoardFullLadderTickers(sourceId));
      return games.size === 0 ? [] : pickUsLineTickers([...heldRows.filter(isUsRow), ...heldUsLines], new Map(), games);
    };
    const cycle = startIndexCycle<BoardIndexPick>({
      sport,
      fetchPick: async (rest, signal) => {
        const moneyline = await fetchMarketsIndex(rest, { sport: wire, classes: ['moneyline'] }, { signal });
        let rows = moneyline;
        if (reading === 'spread-total') {
          const games = boardGameKeys(moneyline, dayWindow);
          if (games.length > 0) rows = moneyline.concat(await fetchMarketsIndex(rest, { sport: wire, classes: ['spread', 'total'], games }, { signal }));
        }
        heldRows = rows;
        // One owner for K/Intl and US: the same index names the US rows; publish
        // their identity and retire a split owner's older read. An index with
        // no US rows clears this key instead (never an empty authority that
        // would pre-empt a split owner's US read).
        const us = rows.filter(isUsRow);
        if (!stopped) {
          if (us.length > 0) { store().setUsSlugIdentity(identityPick, us); store().clearUsSlugIdentity(identityUs); }
          else store().clearUsSlugIdentity(identityPick);
        }
        return { pick: pickBoardTickers(rows, { reading, dayWindow }), moneyline, rows };
      },
      pickTickers: ({ pick, moneyline, rows }) => [...pick.tickers, ...usBoardTickers(moneyline, boardGameKeys(moneyline, dayWindow)),
        ...(lines ? pickUsLineTickers(rows, pick.windows) : [])],
      // A venue split means the K/Intl index cannot name US rows: the US
      // owner's small moneyline catalogue joins on the SAME 60 s cadence. No
      // quote polling or broadening of ladders.
      // #1231: moneyline first; then, on the Spread/Total reading, the US
      // full-game lines of the slate's games, picked at the drawn windows. A
      // failed or slow line read keeps the moneyline names and the held lines.
      fetchUsTickers: async (rest, { moneyline, pick }, signal) => {
        const games = boardGameKeys(moneyline, dayWindow);
        const usMoneyline = await fetchMarketsIndex(rest, { sport: wire, classes: ['moneyline'] }, { signal });
        const names = usBoardTickers(usMoneyline, games);
        if (!lines || games.length === 0) {
          if (!stopped) store().setUsSlugIdentity(identityUs, usMoneyline);
          return names;
        }
        const t0 = performance.now();
        let error: string | null = null;
        try {
          heldUsLines = await fetchMarketsIndex(rest, { sport: wire, classes: ['spread', 'total'], games }, { signal });
        } catch (e) {
          error = e instanceof Error ? e.message : String(e);
        }
        const lineNames = pickUsLineTickers(heldUsLines, pick.windows);
        if (!stopped) {
          store().setUsSlugIdentity(identityUs, [...usMoneyline, ...heldUsLines]);
          setStatus({ usLines: { names: lineNames.length, indexRows: error ? null : heldUsLines.length,
            indexMs: error ? null : Math.round(performance.now() - t0), error } });
        }
        return [...names, ...lineNames];
      },
      broad: () => declareNeeds(broad),
      // Quiet, never broad: an empty allow-list on the edge; the core floor
      // carries the sport.
      muted: (provisional) => declareNeeds(provisional
        ? { ...broad, tickers: [], lineScope: 'main', provisional: true }
        : { ...broad, tickers: [], lineScope: 'main' }),
      tickers: (names) => declareNeeds({ ...broad, tickers: withFullLadders(sourceId, [...names, ...usWorkedNames()]), lineScope: 'main' }),
      lineScope: () => declareNeeds({ ...broad, lineScope: 'main' }),
      onState: (st, landed) => setStatus({
        mode: st.mode, owner: st.owner, tickers: st.tickers, error: st.error, indexedAtMs: st.indexedAtMs, pending: st.pending,
        ...(landed ? { games: landed.pick.games, maxRowsPerGame: landed.pick.maxRowsPerGame } : {}),
      }),
    });
    // A worked game's ladder joins (or leaves) the declaration at once — no
    // index round-trip: the pick stands, only the union changes.
    const offLadder = onFullLadderChange(sourceId, () => { if (status.mode === 'tickers') cycle.redeclare(); });
    return () => {
      stopped = true;
      offLadder();
      cycle.stop();
      store().clearUsSlugIdentity(identityPick);
      store().clearUsSlugIdentity(identityUs);
    };
    // `broad`, `dayWindow` are folded into their keys by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, scoped, sport, reading, windowKey, broadKey]);
  useEffect(() => () => { declareFeedNeeds(sourceId, null); _status.delete(sourceId); lastRef.current = null; }, [sourceId]);
}
